import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHandshakeAngle } from "@fortawesome/free-solid-svg-icons";
import { BsArrowRightCircleFill } from "react-icons/bs";
import { IoMdCall } from "react-icons/io";
import { motion } from "framer-motion";

import logo from "../../assets/logoblack.png";
import "./Prooducts.css";
import { roofing_sheets, Accessories, Rollor_Door, Steel } from "../../product-db";
import { useCikContext } from "../../Context/CentralIndustrisContaxtProvider";

/* ─── Main Products Container ───────────────────────── */
function Prooducts() {
  const { mainprodcat, setMainProdCat, prod } = useCikContext();

  const handleCategoryChange = (cat) => {
    setMainProdCat(cat);
  };

  return (
    <section className="products" id="Products">
      <div className="container">
        <motion.div 
          className="products__header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="section-label">
            <FontAwesomeIcon icon={faHandshakeAngle} />
            Our Products
          </div>
          <h2 className="products__title">What We Offer</h2>
          <p className="products__desc">
            Explore our extensive range of high-quality roofing sheets, accessories, 
            roller doors, and steel structures designed for maximum durability.
          </p>
        </motion.div>

        {/* ── Category Navigation ── */}
        <div className="products__tabs">
          {["Roofing", "Accessories", "Rollor Gates", "Steel"].map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              className={`products__tab ${mainprodcat === cat ? "active" : ""}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* ── Render Category Context ── */}
        {mainprodcat === "Roofing" && prod?.images && <RoofingCategory />}
        {mainprodcat === "Accessories" && prod?.images && <AccessoriesCategory />}
        {mainprodcat === "Rollor Gates" && prod?.images && <RollorDoorCategory />}
        {mainprodcat === "Steel" && prod?.images && <SteelCategory />}
      </div>
    </section>
  );
}

/* ─── Reusable Product Viewer ────────────────────────── */
const ProductViewer = ({ options, onSelect }) => {
  const { prod, imageIndex, setImageIndex, setEachImageStatus, setEachImage } = useCikContext();

  if (!prod) return null;

  return (
    <div className="product-category">
      
      {/* Options Nav */}
      <div className="products__options">
        {options.map((opt) => (
          <button
            key={opt.name}
            className={`products__option ${prod.tittle === opt.titleMatch ? "active" : ""}`}
            onClick={opt.onClick}
          >
            {opt.name}
          </button>
        ))}
      </div>

      {/* Product Detail Card */}
      <motion.div 
        className="product-detail"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        key={prod.tittle} // Remounts to trigger animation on change
      >
        <div className="product-detail__inner">
          
          {/* Gallery Side */}
          <div className="product-gallery">
            <div 
              className="product-gallery__main"
              onClick={() => {
                setEachImageStatus(true);
                setEachImage(prod.images[imageIndex]?.img || prod.images[imageIndex]);
              }}
            >
              {/* If colorCode exists, it's an object, otherwise it's just a string line in DB */}
              {prod.images[imageIndex]?.colorCode && (
                <div 
                  className="product-gallery__color-badge"
                  style={{ background: prod.images[imageIndex].colorCode }}
                >
                  {prod.images[imageIndex].name}
                </div>
              )}
              <img 
                src={prod.images[imageIndex]?.img || prod.images[imageIndex]} 
                alt={prod.tittle} 
              />
            </div>

            {/* Thumbnails */}
            {prod.images.length > 1 && (
              <div className="product-gallery__thumbnails">
                {prod.images.map((imgData, idx) => (
                  <button 
                    key={idx}
                    className={`product-gallery__thumb ${idx === imageIndex ? "active" : ""}`}
                    onClick={() => setImageIndex(idx)}
                    aria-label={`View image ${idx + 1}`}
                  >
                    <img src={imgData.img || imgData} alt="Thumbnail view" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info Side */}
          <div className="product-info">
            <h3 className="product-info__title">{prod.tittle}</h3>
            
            <div className="product-info__section">
              <h4>Description</h4>
              <p className="product-info__desc">{prod.description}</p>
            </div>

            <div className="product-info__section">
              <h4>Benefits</h4>
              <div className="product-info__benefits">
                {prod.benifits?.map((benefit, i) => (
                  <div key={i} className="product-info__benefit">
                    <BsArrowRightCircleFill aria-hidden="true" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {prod.thikness && prod.thikness.length > 0 && (
              <div className="product-info__section">
                <h4>Available Thickness / Dimensions</h4>
                <div className="product-info__thickness">
                  {prod.thikness.map((thick, idx) => (
                    <span key={idx} className="product-info__thickness-chip">{thick}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="product-info__actions">
              <a 
                href={`https://wa.me/+94763607727?text=Is ${prod.tittle} available now?`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary"
              >
                <IoMdCall />
                <span>Contact For Quote</span>
              </a>
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
};

/* ─── Category Providers ────────────────────────────── */

const RoofingCategory = () => {
  const { setProd, setImageIndex } = useCikContext();
  
  useEffect(() => { setProd(roofing_sheets.roof_sheet); }, [setProd]);

  const select = (item) => {
    setImageIndex(0);
    setProd(item);
  };

  const options = [
    { name: "Normal Roof Sheet", titleMatch: "Normal Roof Sheet", onClick: () => select(roofing_sheets.roof_sheet) },
    { name: "Tile Roof Sheet", titleMatch: "Tile Roof Sheet", onClick: () => select(roofing_sheets.tile_roof_sheet) },
    { name: "Gate Cladding Sheet", titleMatch: "Gate Cladding Sheet", onClick: () => select(roofing_sheets.gate_galldin_sheet) },
    { name: "Curve Roof Sheet", titleMatch: "Curve Roof Sheet", onClick: () => select(roofing_sheets.curve_roof_sheet) },
    { name: "Arch Roof Sheet", titleMatch: "Arch Roof Sheet", onClick: () => select(roofing_sheets.arch_roof_sheet) },
  ];

  return <ProductViewer options={options} />;
};

const AccessoriesCategory = () => {
  const { setProd, setImageIndex } = useCikContext();

  useEffect(() => { setProd(Accessories.gutters); setImageIndex(0); }, [setProd, setImageIndex]);

  const select = (item) => {
    setImageIndex(0);
    setProd(item);
  };

  const options = [
    { name: "Gutters", titleMatch: "Gutters", onClick: () => select(Accessories.gutters) },
    { name: "Vadimbu", titleMatch: "Vadimbu", onClick: () => select(Accessories.Vadimbu) },
    { name: "Nozzel", titleMatch: "Nozzel", onClick: () => select(Accessories.Nozzel) },
    { name: "End Cap", titleMatch: "End Cap", onClick: () => select(Accessories.EndCap) },
    { name: "Down Pipe", titleMatch: "Down Pipe", onClick: () => select(Accessories.Down_Pipe) },
    { name: "Ridge Cap", titleMatch: "Ridge Cup", onClick: () => select(Accessories.RidgeCup) },
    { name: "Valley Gutters", titleMatch: "Valley Gutters", onClick: () => select(Accessories.VallyGutters) },
    { name: "Gutter Flashing", titleMatch: "Gutter Flashing", onClick: () => select(Accessories.GutterFalshing) },
  ];

  return <ProductViewer options={options} />;
};

const RollorDoorCategory = () => {
  const { setProd, setImageIndex } = useCikContext();

  useEffect(() => { setProd(Rollor_Door.Auto_Rollor_Door); setImageIndex(0); }, [setProd, setImageIndex]);

  const select = (item) => {
    setImageIndex(0);
    setProd(item);
  };

  const options = [
    { name: "Auto Rollor Door", titleMatch: "Auto Rollor Door", onClick: () => select(Rollor_Door.Auto_Rollor_Door) },
    { name: "Manual Rollor Door", titleMatch: "Manual Rollor Door", onClick: () => select(Rollor_Door.Manual_Rollor_Door) },
  ];

  return <ProductViewer options={options} />;
};

const SteelCategory = () => {
  const { setProd, setImageIndex } = useCikContext();

  useEffect(() => { setProd(Steel.stirrup); setImageIndex(0); }, [setProd, setImageIndex]);

  const select = (item) => {
    setImageIndex(0);
    setProd(item);
  };

  const options = [
    { name: "Stirrup", titleMatch: "Stirrup", onClick: () => select(Steel.stirrup) },
    { name: "C-purlin", titleMatch: "C-purlin", onClick: () => select(Steel.cpurlin) },
  ];

  return <ProductViewer options={options} />;
};

export default Prooducts;
